import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { QuickSetupSection } from "@/redux/slices/quickSetup";
import { resetQuickSetupNavigation, setQuickSetupNavigationFlag } from "@/redux/slices/quickSetup/quickSetupSlice";
import { selectShouldShowBanner, selectLastClickedSection } from "@/redux/slices/quickSetup/selectors";
import { isPathRelatedToSection } from "../utils/sectionMappings";

interface QuickSetupNavigationState {
  cameFromQuickSetup: boolean;
  resetNavigationState: () => void;
  navigateToQuickSetup: () => void;
  setQuickSetupFlag: (section?: QuickSetupSection) => void;
}

const QUICK_SETUP_PATH = "/quick-setup";

export const useQuickSetupNavigation = (): QuickSetupNavigationState => {
  const dispatch = useAppDispatch();
  const cameFromQuickSetup = useAppSelector(selectShouldShowBanner);
  const lastClickedSection = useAppSelector(selectLastClickedSection);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const currentPath = location.pathname;
    const isQuickSetupPath = currentPath.includes(QUICK_SETUP_PATH);

    // Hide banner when back on Quick Setup page
    if (isQuickSetupPath && cameFromQuickSetup) {
      dispatch(resetQuickSetupNavigation());
      return;
    }

    // Hide banner when navigating to pages unrelated to the clicked section
    if (!isQuickSetupPath && cameFromQuickSetup && lastClickedSection) {
      const isRelatedPath = isPathRelatedToSection(currentPath, lastClickedSection);
      if (!isRelatedPath) {
        dispatch(resetQuickSetupNavigation());
      }
    }
  }, [location.pathname, dispatch, cameFromQuickSetup, lastClickedSection]);

  const resetNavigationState = useCallback(() => {
    dispatch(resetQuickSetupNavigation());
  }, [dispatch]);

  const navigateToQuickSetup = useCallback(() => {
    dispatch(resetQuickSetupNavigation());
    navigate(QUICK_SETUP_PATH);
  }, [navigate, dispatch]);

  const setQuickSetupFlag = useCallback(
    (section?: QuickSetupSection) => {
      dispatch(setQuickSetupNavigationFlag({ section }));
    },
    [dispatch]
  );

  return {
    cameFromQuickSetup,
    resetNavigationState,
    navigateToQuickSetup,
    setQuickSetupFlag,
  };
};
